import { useMutation } from '@apollo/client';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import DateTimePicker from 'react-datetime';
import { LoadingLogo } from '../../../../devtools/components/Modal';
import { REMOVE_CALENDAR, UPDATE_CALENDAR } from '../graphql';
import { LevelRewardInput, PaidCalendarReward, type PaidCalendar } from '../../../../../../__gqltypes__';
import CalendarForm, { isVanityItem } from './CalendarForm';
import DeleteVanityModal from '../../Items/components/DeleteVanityModal';
import EditRewardsModal from '../../../components/EditRewardsModal';
import Chip from '../../../components/Chip';
import { emitter } from '../../../../../../apollo';

function userIdsArrayToString(userIdsArray: string[] | string): string {
  if (Array.isArray(userIdsArray)) {
    return userIdsArray.join(', ');
  }
  return userIdsArray;
}

function userIdsStringToArray(songIdsString: string): string[] | undefined {
  if (typeof songIdsString === 'string' && songIdsString.trim().length > 0) {
    return songIdsString.split(',').map((id) => id.trim());
  }
  return [];
}

type Props = {
  calendar: PaidCalendar;
  refetch: () => void;
};

export default function CalendarView({ calendar, refetch }: Props) {
  const [calendarData, setCalendar] = useState(calendar);
  const [showEditReward, setShowEditReward] = useState(false);

  const handleShowEditReward = () => {
    setShowEditReward(true);
  };

  const handleAddReward = useCallback(
    (newReward: LevelRewardInput) => {
      if (!isVanityItem.has(newReward.type) && newReward.amount <= 0) {
        emitter.emit('error', [{ message: `Wrong ${newReward.type} amount value`, locations: 'purchaseReward' }]);
        setShowEditReward(false);
        return;
      }
      setCalendar({
        ...calendarData,
        purchaseReward: {
          type: newReward.type,
          amount: newReward.amount,
          itemId: newReward.itemId ?? '',
          __typename: 'PaidCalendarReward',
        },
      });
      setShowEditReward(false);
    },
    [calendarData]
  );

  useEffect(() => {
    setCalendar(calendar);
  }, [calendar]);
  const [updateCalendar, { loading }] = useMutation(UPDATE_CALENDAR, {
    onCompleted: () => refetch(),
  });
  const history = useHistory();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [deleteCalendar] = useMutation(REMOVE_CALENDAR, {
    onCompleted: () => refetch(),
  });

  const handleUpdate = () => {
    updateCalendar({
      variables: {
        id: calendarData?.id,
        name: calendarData?.name,
        calendarItems: calendarData?.calendarItems?.map((r) => _.omit(r, '__typename')),
        skuId: calendarData?.skuId,
        startDate: calendarData?.startDate,
        endDate: calendarData?.endDate,
        purchaseReward: calendarData?.purchaseReward
          ? _.omit(calendarData?.purchaseReward, '__typename')
          : calendarData?.purchaseReward,
        filterVersion: calendarData?.filterVersion,
        filterUserIds: calendarData?.filterUserIds,
      },
    }).catch((e) => {
      console.log(e.message);
    });
  };

  const handleDelete = () => {
    deleteCalendar({ variables: { id: calendarData?.id } });
    history.push('/sp3/liveops/calendars/:id');
    setShowDeleteModal(false);
  };

  const handleAddLevel = () => {
    const levels = calendarData?.calendarItems?.length ? [...calendarData.calendarItems] : [];
    levels.push({ type: 'CURRENCY_DIAMOND', amount: 1, itemId: '', __typename: 'PaidCalendarReward' });
    setCalendar({ ...calendarData, calendarItems: levels });
  };

  const handleDeleteLevel = () => {
    const levels = calendarData?.calendarItems?.length ? [...calendarData.calendarItems] : [];
    levels.pop();
    setCalendar({ ...calendarData, calendarItems: levels });
  };

  const iterate = (root: Record<string, string> | string, acc: string[]) => {
    if (_.isString(root)) {
      if (root.startsWith('https')) {
        acc.push(root);
      }
      return;
    }
    if (_.isArray(root)) {
      root.forEach((r) => iterate(r, acc));
      return;
    }

    if (!_.isObject(root)) {
      return;
    }

    const val = Object.values(root);
    val.forEach((v) => iterate(v, acc));
  };

  const skus = [
    'special.calendar.1',
    'special.calendar.2',
    'special.calendar.3',
    'special.calendar.4',
    'special.calendar.5',
    'special.calendar.6',
  ];

  return (
    <>
      <Card className="mt-3">
        <LoadingLogo show={loading} />
        <DeleteVanityModal
          type="Calendar"
          name={calendarData?.name}
          show={showDeleteModal && !loading}
          onHide={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
        />
        <Card.Header className="text-center d-flex justify-content-between">
          <h4>{calendarData?.name}</h4>
          <div>
            <Button className="mr-3" variant="danger" onClick={() => setShowDeleteModal(true)}>
              Delete
            </Button>
            <Button onClick={handleUpdate}>Save</Button>
          </div>
        </Card.Header>
        <Card.Body style={{ height: '100%' }}>
          <Form>
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control
                placeholder=""
                value={calendarData?.name}
                onChange={(e) => setCalendar({ ...calendarData, name: e.target.value })}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Sku</Form.Label>
              <Form.Control
                as="select"
                type="text"
                value={calendarData?.skuId ?? ''}
                onChange={(e) => setCalendar({ ...calendarData, skuId: e.target.value })}
              >
                <option value="" />
                {skus.map((sku, index) => (
                  <option key={index} value={sku}>
                    com.freshplanet.sp3.ios.{sku}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group>
              <Form.Label>Start date</Form.Label>
              <DateTimePicker
                onChange={(e) => setCalendar({ ...calendarData, startDate: e })}
                value={calendarData?.startDate ?? undefined}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>End date</Form.Label>
              <DateTimePicker
                onChange={(e) => setCalendar({ ...calendarData, endDate: e })}
                value={calendarData?.endDate ?? undefined}
              />
            </Form.Group>
            <Form.Group>
              <EditRewardsModal
                isEdit={false}
                tier={null}
                show={showEditReward}
                level={null}
                reward={calendarData?.purchaseReward as LevelRewardInput}
                handleSave={handleAddReward}
                handleClose={() => setShowEditReward(false)}
                disableCurrencySpecialEvent
                disableSubscriptionDay={false}
                disableMonthlyBuff
                enableExtendedFragmentSelector
              />
              <Form.Label>Reward on purchase</Form.Label>
              <br />
              <Chip
                canDelete
                isAdd={!calendarData?.purchaseReward}
                btnAction={() => setCalendar({ ...calendarData, purchaseReward: null })}
                reward={calendarData?.purchaseReward as LevelRewardInput}
                globalAction={handleShowEditReward}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                Version: <br />
                (&quot;3.020.008&quot; write as 3020008)
              </Form.Label>
              <Form.Control
                as="input"
                type="number"
                value={calendarData?.filterVersion ?? 0}
                onChange={(e) => setCalendar({ ...calendarData, filterVersion: +e.target.value })}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>
                User Ids: <br />
                (separated by commas &quot;,&quot;)
              </Form.Label>
              <Form.Control
                as="textarea"
                value={userIdsArrayToString(calendarData?.filterUserIds ?? [])}
                onChange={(e) =>
                  setCalendar({ ...calendarData, filterUserIds: userIdsStringToArray(e.target.value) ?? [] })
                }
              />
            </Form.Group>
            <hr />
          </Form>
          {calendarData?.calendarItems?.map((_level: PaidCalendarReward, index: number) => (
            <CalendarForm
              index={index}
              level={_level}
              setReward={(lvl) => {
                const newLevels = [...calendarData.calendarItems];
                newLevels[index] = lvl;
                setCalendar({ ...calendarData, calendarItems: newLevels });
              }}
              key={calendarData.id + index}
            />
          ))}
          <br />
          <div className="d-flex justify-content-between">
            <Button variant="success" onClick={handleAddLevel}>
              <i className="fas fa-plus-circle mr-2" />
              Add Day
            </Button>
            <Button variant="danger" onClick={handleDeleteLevel}>
              <i className="fas fa-minus-circle mr-2" /> Remove Day
            </Button>
          </div>
        </Card.Body>
      </Card>
    </>
  );
}
